import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Package, Zap, ShieldCheck, Truck, Globe2 } from "lucide-react";

const HERO_FADE_IN = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

const HERO_BG = "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjBzaGlwcGluZyUyMGNvbnRhaW5lcnN8ZW58MHx8fHwxNzgzOTI2MTIyfDA&ixlib=rb-4.1.0&q=85";
const WAREHOUSE_BG = "https://images.pexels.com/photos/9305407/pexels-photo-9305407.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CATEGORIES = [
  { name: "Electronics", img: "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
  { name: "Fashion", img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
  { name: "Home & Garden", img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
  { name: "Industrial", img: "https://images.unsplash.com/photo-1565043666747-69f6646db940?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
  { name: "Beauty", img: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
  { name: "Sports", img: "https://images.unsplash.com/photo-1517649763962-0c623066013b?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
];

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [enterprises, setEnterprises] = useState([]);

  useEffect(() => {
    api.get("/products?limit=20").then(r => setProducts(r.data)).catch(() => {});
    api.get("/enterprises").then(r => setEnterprises(r.data)).catch(() => {});
  }, []);

  const flashDeals = products.filter(p => p.flash_sale).slice(0, 4);
  const featured = products.filter(p => p.marketplace === "b2c").slice(0, 10);
  const wholesale = products.filter(p => p.marketplace === "b2b").slice(0, 4);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950 text-white grain">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/70 to-blue-950/70" />
        </div>
        <div className="relative nx-container py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <motion.div {...HERO_FADE_IN}>
              <span className="mono text-[11px] text-[#A3E635] border border-[#A3E635] px-3 py-1">GLOBAL/MARKETPLACE/2026</span>
              <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter">
                Where <span className="text-[#A3E635]">enterprises</span><br />
                meet <span className="italic font-light">everyday</span> buyers.
              </h1>
              <p className="mt-6 max-w-xl text-slate-300 text-lg leading-relaxed">
                Source in bulk from verified suppliers or grab retail flash deals — all on one radically unified platform.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/products" data-testid="hero-shop-btn" className="nx-btn-accent inline-flex items-center gap-2">
                  Shop Retail <ArrowRight size={16} />
                </Link>
                <Link to="/products?marketplace=b2b" data-testid="hero-wholesale-btn" className="border border-white/50 hover:border-white px-6 py-3 text-white font-bold text-sm mono">
                  EXPLORE/WHOLESALE
                </Link>
              </div>
              <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
                <div><div className="text-3xl font-black">10M+</div><div className="mono text-[10px] text-slate-400">PRODUCTS</div></div>
                <div><div className="text-3xl font-black">200K</div><div className="mono text-[10px] text-slate-400">SUPPLIERS</div></div>
                <div><div className="text-3xl font-black">200+</div><div className="mono text-[10px] text-slate-400">COUNTRIES</div></div>
              </div>
            </motion.div>
          </div>
          <div className="md:col-span-4">
            <div className="bg-white text-slate-900 p-6 border-l-4 border-[#A3E635]">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={16} className="text-emerald-700" />
                <span className="mono text-[11px] text-emerald-700">LIVE/FLASH/DEALS</span>
              </div>
              <h3 className="font-black text-2xl tracking-tight mb-4">Ends in 04:22:19</h3>
              <div className="space-y-3">
                {flashDeals.slice(0, 3).map(p => (
                  <Link key={p.product_id} to={`/product/${p.product_id}`} data-testid={`hero-deal-${p.product_id}`} className="flex gap-3 items-center hover:bg-slate-50 p-2 -mx-2 transition-colors">
                    <img src={p.image} alt="" className="w-14 h-14 object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{p.name}</p>
                      <p className="text-sm"><span className="font-black text-blue-700">${p.price.toFixed(2)}</span> <span className="mono text-[10px] text-[#84CC16]">-{p.discount}%</span></p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Bottom trust bar */}
        <div className="relative border-t border-white/10">
          <div className="nx-container py-4 flex flex-wrap gap-6 justify-between text-xs text-slate-400">
            <span className="flex items-center gap-2"><ShieldCheck size={14} /> Trade Assurance</span>
            <span className="flex items-center gap-2"><Truck size={14} /> Global Logistics</span>
            <span className="flex items-center gap-2"><Package size={14} /> Verified Suppliers</span>
            <span className="flex items-center gap-2"><Globe2 size={14} /> Multi-currency</span>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="nx-container py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="mono text-[11px] text-slate-500">01/CATEGORIES</span>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter mt-2">Shop by industry</h2>
          </div>
          <Link to="/products" data-testid="all-categories-link" className="mono text-xs hover:text-blue-700">ALL/→</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {CATEGORIES.map(c => (
            <Link key={c.name} to={`/products?category=${encodeURIComponent(c.name)}`} data-testid={`category-${c.name}`} className="nx-card group aspect-square relative overflow-hidden">
              <img src={c.img} alt={c.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100" style={{ transition: "opacity 0.3s ease, transform 0.4s ease" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white font-black text-sm">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FLASH DEALS */}
      {flashDeals.length > 0 && (
        <section className="bg-slate-950 text-white py-16 grain relative overflow-hidden">
          <div className="nx-container">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="mono text-[11px] text-[#A3E635]">02/URGENT</span>
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter mt-2">Flash deals ending soon</h2>
              </div>
              <div className="mono text-xs text-slate-400">LIVE/NOW</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {flashDeals.map(p => <ProductCard key={p.product_id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED PRODUCTS */}
      <section className="nx-container py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="mono text-[11px] text-slate-500">03/RETAIL</span>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter mt-2">Trending this week</h2>
          </div>
          <Link to="/products" data-testid="all-retail-link" className="mono text-xs hover:text-blue-700">VIEW/ALL/→</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {featured.map(p => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* ENTERPRISE SHOWCASE */}
      <section className="relative bg-slate-100 py-16">
        <div className="nx-container grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5 relative">
            <img src={WAREHOUSE_BG} alt="warehouse" className="w-full h-full object-cover border border-slate-300" />
          </div>
          <div className="md:col-span-7">
            <span className="mono text-[11px] text-blue-700">04/WHOLESALE</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter mt-2 mb-4">Enterprise-grade suppliers.</h2>
            <p className="text-slate-700 max-w-lg mb-8">Verified factories, bulk pricing, ISO-certified quality. Direct-to-container logistics on every deal.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {enterprises.slice(0, 4).map(e => (
                <div key={e.enterprise_id} data-testid={`enterprise-${e.enterprise_id}`} className="nx-card p-4">
                  <div className="aspect-video bg-slate-100 mb-3 overflow-hidden">
                    <img src={e.logo} alt="" className="w-full h-full object-cover" />
                  </div>
                  <p className="mono text-[10px] text-slate-500">{e.country}/{e.years}Y</p>
                  <p className="font-bold text-sm mt-1 line-clamp-2">{e.name}</p>
                  <p className="text-xs text-slate-600 mt-1">{e.specialty}</p>
                  {e.verified && <span className="nx-tag text-blue-700 border-blue-700 mt-2">✓ VERIFIED</span>}
                </div>
              ))}
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {wholesale.slice(0, 4).map(p => (
                <Link key={p.product_id} to={`/product/${p.product_id}`} data-testid={`wholesale-${p.product_id}`} className="nx-card p-3">
                  <img src={p.image} alt="" className="w-full aspect-square object-cover mb-2" />
                  <p className="text-xs font-bold line-clamp-2">{p.name}</p>
                  <p className="font-black text-blue-700 mt-1">${p.price.toFixed(0)} <span className="mono text-[9px] text-slate-500">MOQ:{p.moq}</span></p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#A3E635] text-emerald-950 py-16">
        <div className="nx-container flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter">Ready to source globally?</h2>
            <p className="mt-2 text-emerald-900">Join 8M+ buyers already trading on Nexara.</p>
          </div>
          <Link to="/register" data-testid="cta-join-btn" className="bg-slate-950 text-white px-8 py-4 font-bold hover:bg-slate-800" style={{transition:"background-color 0.2s ease"}}>
            Create Free Account →
          </Link>
        </div>
      </section>
    </div>
  );
}
